import { defined } from '@platform/schema';
import { isOnboardingProposal, proposedEdits } from '@platform/schema/chat';
import { sceneBoundsRefusal } from '@platform/schema/publish-rules';

import { answerFields, keyedAnswers } from './answers';
import {
  editableItems,
  FIELD_NAMES,
  sceneBody,
  sceneFieldName,
  sceneKindName,
} from './edits';
import { addedAt, arcAfter, teachingScenes } from './order';
import { personaName, personaOrder } from './personas';

import type { KeyedAnswer } from './answers';
import type { LessonWrite } from './edits';
import type { AskPlannerResponse } from '@platform/client';
import type { Change } from '@platform/schema/changes';
import type {
  ArcChange,
  LessonProposal,
  ProposedEdit,
} from '@platform/schema/chat';
import type { LessonWorkspace } from '@platform/schema/review';

/**
 * What the console does with an edit the planner suggested — GEN-66.
 *
 * Two pure functions and no third: the write the operator would make, and what they would
 * be changing. Everything after that is machinery the console already has —
 * `useEditing.save` sends it, `undoStep` derives the way back, and `UndoRedo` offers it.
 * That is the whole reason a proposal is shaped like a `LessonWrite` rather than like
 * something new: applying one is not a new kind of write, so it needs no new undo.
 */

/**
 * The proposals on an answer, in a shape that can be told apart.
 *
 * Parsed rather than cast: the generated SDK renders a `const` discriminator as plain
 * `string`, so an entry is three shapes TypeScript cannot choose between. The check is
 * real rather than a formality — the console is about to write these, and a shape the
 * schema refuses is one no `PATCH` would have taken either.
 */
export const proposalsFrom = (answer: AskPlannerResponse): ProposedEdit[] =>
  proposedEdits(answer.proposals);

/**
 * The write that applies this proposal.
 *
 * A total function, and a dull one: the shapes line up one-to-one, because both are
 * picked from the same `update*` schemas. Nothing is translated, which is what keeps
 * applying off a second write path — `serviceWriter`'s concern, one level up.
 *
 * `defined` is not a workaround here. Zod infers `key?: T | undefined` and the SDK's DTO
 * is `key?: T`; dropping the absent keys is also the PATCH semantics the body wants.
 */
export function proposalWrite(edit: LessonProposal): LessonWrite {
  const { lessonId } = edit;

  if (edit.kind === 'lesson')
    return { kind: 'lesson', lessonId, body: defined(edit.body) };

  if (edit.kind === 'scene') {
    // `keys` is mapped through `defined` as well: it is shallow, and an entry re-keying a
    // CHOICE carries `correctAmount: undefined` the DTO refuses under
    // `exactOptionalPropertyTypes` — the same shape `editing.ts` drops for `personas`.
    const { keys, ...body } = edit.body;

    return {
      kind: 'scene',
      lessonId,
      sceneId: edit.sceneId,
      body: defined({
        ...body,
        ...(keys === undefined ? {} : { keys: keys.map(defined) }),
      }),
    };
  }

  if (edit.kind === 'arc')
    return { kind: 'arc', lessonId, change: edit.change };

  return {
    kind: 'question',
    lessonId,
    questionId: edit.questionId,
    explanation: edit.body.explanation,
  };
}

/**
 * Why this proposal cannot be applied, or nothing — GEN-77.
 *
 * Only the arc has one. A field write is refused by the endpoint if it is going to be
 * refused at all, but which scenes a lesson holds is bounded by a rule `placeScenes` does
 * not check — it is the publish gate's — so an applied add could leave the lesson
 * unpublishable with nothing on screen having said so. The Add and Remove buttons have
 * always read the same bound before offering themselves; this is the chat reading it.
 */
export function proposalRefusal(
  lesson: LessonWorkspace,
  edit: LessonProposal,
): string | undefined {
  return edit.kind === 'arc'
    ? sceneBoundsRefusal(
        teachingScenes(lesson.scenes),
        teachingScenes(arcAfter(edit.change, lesson.scenes)),
      )
    : undefined;
}

/**
 * The personas, as the reviewer reads them rather than as the enum spells them — GEN-73.
 *
 * One string for the whole set, because `personas` replaces the set: a diff row per
 * persona would offer three changes where the write is one. In 015's order and not the
 * order they arrived, so a set the planner listed differently does not read as a reorder.
 */
const personaList = (labels: readonly { persona: string }[]): string =>
  [...labels]
    .sort((a, b) => personaOrder(a.persona) - personaOrder(b.persona))
    .map(({ persona }) => personaName(persona))
    .join(', ');

/**
 * Each entry the proposal sent, against the rows the scene actually holds.
 *
 * `undefined` for an id the scene does not hold, which both callers treat the same way:
 * `updateScene` scopes its writes to the scene, so applying one would change nothing at
 * all, and a diff row promising a change that cannot happen is worse than no row.
 */
function against<Sent extends { id: string }, Known>(
  sent: readonly Sent[] | undefined,
  known: Map<string, Known>,
  row: (one: Sent, was: Known) => Change,
): Change[] | undefined {
  const rows: Change[] = [];

  for (const one of sent ?? []) {
    const was = known.get(one.id);
    if (was === undefined) return undefined;

    rows.push(row(one, was));
  }

  return rows;
}

type Keyed = Omit<KeyedAnswer, 'label'>;
type KeyColumn = 'isCorrect' | 'correctPosition' | 'correctAmount';

/**
 * Which column a re-key is about — GEN-77.
 *
 * Decided once per row, because both sides of it have to be read off the *same* column: a
 * CHOICE keyed on `isCorrect` rendered against an amount would be a diff between two
 * different questions.
 */
function keyColumn(sent: Keyed): KeyColumn {
  if (sent.isCorrect !== undefined) return 'isCorrect';

  return sent.correctPosition === undefined
    ? 'correctAmount'
    : 'correctPosition';
}

/** One side of that row, as the operator reads it rather than as the column stores it. */
function keyRead(held: Keyed, column: KeyColumn): string {
  if (column === 'isCorrect')
    return held.isCorrect === true ? 'Correct' : 'Not correct';

  if (column === 'correctPosition')
    return held.correctPosition === undefined
      ? 'Unplaced'
      : `Position ${String(held.correctPosition)}`;

  return held.correctAmount === undefined ? '—' : String(held.correctAmount);
}

/**
 * The rows that are actually changes.
 *
 * A field the proposal did not carry is dropped, and so is one whose new text is what the
 * lesson already says — a row promising a change that would not happen is the same lie in
 * both cases. Dropping every row is how "the edit is already the text" is reported, and
 * the caller says so rather than offering an apply that writes nothing.
 */
function changed(
  rows: readonly { field: string; before: string; after: string | undefined }[],
): Change[] {
  return rows.flatMap(({ field, before, after }) =>
    after === undefined || after === before ? [] : [{ field, before, after }],
  );
}

/** How a scene is named in an arc row: where it sits, and what mechanic it is. */
const sceneNamed = (position: number, kind: string): string =>
  `Scene ${String(position)} · ${sceneKindName(kind)}`;

/**
 * What an arc change would do, in rows — GEN-77.
 *
 * Written out rather than passed through `changed()`, which drops a row whose "after" is
 * what the lesson already says. That is the right rule for a field and the wrong one here:
 * a removal whose headline happened to read *Removed* is still a removal. The one arc
 * change that can be a no-op is a move to the position the scene is already at, and it is
 * the empty list — the outcome the caller already renders as "the edit is already the
 * lesson".
 */
function arcChanges(
  lesson: LessonWorkspace,
  change: ArcChange,
): Change[] | undefined {
  if (change.op === 'add')
    return [
      {
        field: `A new ${sceneKindName(change.scene.kind)} scene at ${String(
          addedAt(change.position, lesson.scenes.length),
        )}`,
        before: 'Not in the lesson',
        after: change.scene.prompt,
      },
    ];

  const scene = lesson.scenes.find((one) => one.id === change.sceneId);
  if (scene === undefined) return undefined;

  if (change.op === 'remove')
    return [
      {
        field: sceneNamed(scene.position, scene.kind),
        before: scene.prompt,
        after: 'Removed from the lesson',
      },
    ];

  if (change.op === 'move')
    return change.position === scene.position
      ? []
      : [
          {
            field: sceneNamed(scene.position, scene.kind),
            before: `Position ${String(scene.position)}`,
            after: `Position ${String(change.position)}`,
          },
        ];

  return [
    {
      field: `Scene ${String(scene.position)} · mechanic`,
      before: sceneKindName(scene.kind),
      after: sceneKindName(change.scene.kind),
    },
    ...changed([
      {
        field: sceneFieldName(scene.position, 'prompt'),
        before: scene.prompt,
        after: change.scene.prompt,
      },
    ]),
  ];
}

/**
 * What the operator would be changing, old beside new.
 *
 * `undefined` — not an empty list — when the proposal addresses a row this lesson does not
 * have. A regeneration that replaced the scenes while the panel was open is the ordinary
 * way that happens, and the honest thing to do is refuse to offer the apply: the diff
 * would be against a row nobody can see, and applying it would write to a scene the
 * operator is not looking at.
 *
 * An empty list is a different outcome and a real one — a proposal whose new text is what
 * the lesson already says.
 */
export function proposedChanges(
  lesson: LessonWorkspace,
  edit: ProposedEdit,
): Change[] | undefined {
  if (isOnboardingProposal(edit)) return undefined;

  if (edit.kind === 'lesson')
    return changed([
      {
        field: FIELD_NAMES.title ?? 'Title',
        before: lesson.title,
        after: edit.body.title,
      },
      {
        field: FIELD_NAMES.summary ?? 'Summary',
        before: lesson.summary,
        after: edit.body.summary,
      },
      {
        field: FIELD_NAMES.personas ?? 'Personas',
        before: personaList(lesson.personas),
        after: edit.body.personas && personaList(edit.body.personas),
      },
      {
        field: FIELD_NAMES.tags ?? 'Tags',
        before: lesson.tags.join(', '),
        after: edit.body.tags?.join(', '),
      },
    ]);

  if (edit.kind === 'arc') return arcChanges(lesson, edit.change);

  if (edit.kind === 'question') {
    const asked = lesson.scenes.find(
      (one) => 'questionId' in one && one.questionId === edit.questionId,
    );

    if (asked === undefined || !('explanation' in asked)) return undefined;

    return changed([
      {
        field: sceneFieldName(asked.position, 'explanation'),
        before: asked.explanation ?? '',
        after: edit.body.explanation,
      },
    ]);
  }

  const scene = lesson.scenes.find((one) => one.id === edit.sceneId);
  if (scene === undefined) return undefined;

  const items = against(
    edit.body.items,
    new Map(editableItems(scene).map((item) => [item.id, item.label])),
    (item, was) => ({
      field: sceneFieldName(scene.position, 'items'),
      before: was,
      after: item.label,
    }),
  );

  // Two categories share the field name, so the row names the one it moves — a re-key's
  // before and after are both numbers, and nothing else on the row would say which.
  const keys = against(
    edit.body.keys,
    new Map(keyedAnswers(scene).map((one) => [one.id, one])),
    (key, was) => {
      const column = keyColumn(key);

      return {
        field: `${sceneFieldName(scene.position, 'keys')} · ${was.label}`,
        before: keyRead(was, column),
        after: keyRead(key, column),
      };
    },
  );

  if (items === undefined || keys === undefined) return undefined;

  return changed([
    {
      field: sceneFieldName(scene.position, 'prompt'),
      before: scene.prompt,
      after: edit.body.prompt,
    },
    {
      field: sceneFieldName(scene.position, 'body'),
      before: sceneBody(scene),
      after: edit.body.body,
    },
    ...items,
    ...keys,
    // The numeric answer, read off the scene rather than listed here — GEN-73. A kind that
    // does not carry one yields no fields, so a proposal that named `targetMin` on a
    // CHOICE offers no row for it and applies nothing the operator was not shown.
    ...answerFields(scene).map((answer) => ({
      field: sceneFieldName(scene.position, answer.key),
      before: String(answer.value),
      after: edit.body[answer.key]?.toString(),
    })),
  ]);
}
